import { DEFAULT_FLOOR_HEIGHT_M, toDeg, toRad } from './geometry';
import { AISLE_WIDTH_FIXED_M, PARKING_METHOD_KEYS } from './parking/constants';
import { defaultState, defaultZoning } from './state';
import type { SerializedBuilding, SerializedStateV1, State } from './types';

export const STORAGE_KEY = 'mlp_state_v1';

export function serialize(s: State): SerializedStateV1 {
  return {
    version: 1,
    site: s.site,
    buildings: s.buildings.map(
      (b): SerializedBuilding => ({
        id: b.id,
        name: b.name,
        center: b.center,
        width: b.width,
        depth: b.depth,
        angleDeg: toDeg(b.angle),
        floors: b.floors,
        floorHeightM: b.floorHeightM,
      })
    ),
    zoning: s.zoning,
    mapView: s.mapView,
    shadowCheck: s.shadowCheck,
    parking: {
      requiredCars: s.parking.requiredCars,
      targetRatio: s.parking.targetRatio,
      ratioPerUnit: s.parking.ratioPerUnit,
      assumedUnitArea: s.parking.assumedUnitArea,
      stallW: s.parking.stallW,
      stallD: s.parking.stallD,
      keiRatio: s.parking.keiRatio,
      keiStallW: s.parking.keiStallW,
      keiStallD: s.parking.keiStallD,
      aisleW: s.parking.aisleW,
      aisleMinW: s.parking.aisleMinW,
      turnRadius: s.parking.turnRadius,
      siteClearance: s.parking.siteClearance,
      buildingClearance: s.parking.buildingClearance,
      disabledCount: s.parking.disabledCount,
      guestCount: s.parking.guestCount,
      evCount: s.parking.evCount,
      cellSize: s.parking.cellSize,
      angleMode: s.parking.angleMode,
      manualAngleDeg: s.parking.manualAngleDeg,
      rowsAlongXMode: s.parking.rowsAlongXMode,
      roadDirectEdgeIndices: s.parking.roadDirectEdgeIndices,
      methods: Object.fromEntries(
        PARKING_METHOD_KEYS.map((k) => [
          k,
          { enabled: s.parking.methods[k].enabled, zone: s.parking.methods[k].zone },
        ])
      ) as SerializedStateV1['parking']['methods'],
    },
    bicycle: {
      requiredCount: s.bicycle.requiredCount,
      ratioPerUnit: s.bicycle.ratioPerUnit,
      unitArea: s.bicycle.unitArea,
      assumedUnitArea: s.bicycle.assumedUnitArea,
      enabled: s.bicycle.enabled,
      autoZone: s.bicycle.autoZone,
      manualZone: s.bicycle.autoZone ? null : s.bicycle.zone,
    },
  };
}

// biome-ignore lint/suspicious/noExplicitAny: parsed localStorage JSON has no fixed shape until validated field-by-field below
export function deserialize(json: any): State {
  const s = defaultState();
  if (json.site) s.site = Object.assign(s.site, json.site);
  if (!Array.isArray(s.site.edges) || s.site.edges.length !== s.site.points.length) {
    s.site.edges = s.site.points.map(() => ({
      type: 'none' as const,
      roadWidthM: null,
      driveways: [],
    }));
  }
  s.site.edges.forEach((edge) => {
    if (!Array.isArray(edge.driveways)) edge.driveways = [];
  });
  if (Array.isArray(json.buildings)) {
    // biome-ignore lint/suspicious/noExplicitAny: raw JSON building record, shape unchecked until here
    s.buildings = json.buildings.map((b: any) => ({
      id: b.id,
      name: b.name,
      center: b.center,
      width: b.width,
      depth: b.depth,
      angle: toRad(b.angleDeg || 0),
      floors: b.floors,
      floorHeightM: b.floorHeightM != null ? b.floorHeightM : DEFAULT_FLOOR_HEIGHT_M,
    }));
  }
  if (json.zoning) {
    s.zoning = Object.assign(defaultZoning(), json.zoning);
    s.zoning.road = Object.assign(defaultZoning().road, json.zoning.road);
    s.zoning.adjacent = Object.assign(defaultZoning().adjacent, json.zoning.adjacent);
    s.zoning.northSlope = Object.assign(defaultZoning().northSlope, json.zoning.northSlope);
  }
  if (json.mapView) s.mapView = json.mapView;
  if (json.shadowCheck) {
    s.shadowCheck = Object.assign(s.shadowCheck, json.shadowCheck, {
      date: Object.assign(s.shadowCheck.date, json.shadowCheck.date),
    });
  }
  if (json.parking) {
    const { methods, ...restParking } = json.parking;
    Object.assign(s.parking, restParking);
    if (methods) {
      PARKING_METHOD_KEYS.forEach((k) => {
        if (methods[k])
          s.parking.methods[k] = { enabled: !!methods[k].enabled, zone: methods[k].zone || null };
      });
    }
  }
  // 通路幅は固定値。古い保存データ・インポートしたデータに別の値が入っていてもそろえる
  s.parking.aisleW = AISLE_WIDTH_FIXED_M;
  s.parking.aisleMinW = AISLE_WIDTH_FIXED_M;
  // 背合わせ(壁背負い含む)はUI上のON/OFF切り替えを持たない既定方式のため、
  // 古い保存データにenabled:falseが残っていても常に有効化する
  s.parking.methods.backToBack.enabled = true;
  if (json.bicycle) {
    Object.assign(s.bicycle, json.bicycle);
    if (!s.bicycle.autoZone && json.bicycle.manualZone) s.bicycle.zone = json.bicycle.manualZone;
  }
  return s;
}

export function loadState(): State {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    return deserialize(JSON.parse(raw));
  } catch {
    return defaultState();
  }
}

/**
 * Parses a JSON file produced by the export button (or copied from localStorage)
 * and normalizes it through deserialize/serialize, so what gets stored has the
 * same shape as a regular save. Throws an Error with a user-facing message.
 */
export function parseImportedState(text: string): SerializedStateV1 {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error('JSONとして読み込めませんでした。ファイルの内容を確認してください。');
  }
  if (typeof json !== 'object' || json === null || Array.isArray(json)) {
    throw new Error('このアプリの保存データではありません。');
  }
  const obj = json as Record<string, unknown>;
  const site = obj.site as Record<string, unknown> | undefined;
  if (!site || !Array.isArray(site.points) || typeof obj.parking !== 'object') {
    throw new Error('このアプリの保存データではありません(敷地・駐車場の情報が見つかりません)。');
  }
  return serialize(deserialize(obj));
}

/** Writes an imported state as the saved state, cancelling any pending debounced save of the current one. */
export function replaceSavedState(serialized: SerializedStateV1): void {
  if (saveTimer !== null) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(serialized));
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
export function scheduleSave(state: State): void {
  if (saveTimer !== null) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(serialize(state)));
    } catch {
      // ignore quota errors
    }
  }, 400);
}
